import { watch } from 'vue';
import { roleHelpers } from '@/services/utils';
import type {
  ReportDistribution,
  ReportFilterFamily,
  ReportShowFilters,
  ReportTrustAccount,
  ReportUser,
} from '@/models/reportFilterTypes';
import type { useReportFiltersStore } from '@/stores/reports/useReportFiltersStore';

type ReportFiltersStore = ReturnType<typeof useReportFiltersStore>;

export interface ReportPanelUser extends ReportUser {
  role: string;
  permissions: {
    matters: { autoAssign?: boolean };
    billing?: { distributions?: boolean };
  };
  company?: {
    settings?: {
      trustEnabled?: boolean;
      trust?: {
        operating?: ReportTrustAccount;
        accounts?: Array<{ bank: { id: string; name: string } }>;
      };
    };
  };
}

type FeatureFlags = Record<string, boolean | undefined> | undefined;

/** On for the firm (`/me`) or for this build (`env.json` features, for local testing). */
export function reportFilterPanelEnabled(
  meFeatures: FeatureFlags,
  envFeatures: FeatureFlags,
) {
  return Boolean(
    meFeatures?.reportFilterPanel || envFeatures?.reportFilterPanel,
  );
}

interface FeatureUserService {
  me: () => PromiseLike<{ features?: Record<string, boolean> }>;
}

let resolved: { me: unknown; enabled: boolean } | null = null;

/**
 * The one place the flag is decided, so the directives and `generic.ctrl` can't disagree.
 * Tied to the `/me` object it was worked out from: a different user means a fresh answer.
 */
export function resolveReportFilterPanel(
  userService: FeatureUserService,
  envFeatures: FeatureFlags,
): Promise<boolean> {
  return Promise.resolve(userService.me()).then(
    (me) => {
      if (resolved?.me !== me) {
        resolved = {
          me,
          enabled: reportFilterPanelEnabled(me?.features, envFeatures),
        };
      }
      return resolved.enabled;
    },
    () => reportFilterPanelEnabled(undefined, envFeatures),
  );
}

/** The last answer, for a directive that has to attach in the same tick. */
export function knownReportFilterPanel(): boolean | null {
  return resolved ? resolved.enabled : null;
}

export function forgetReportFilterPanel() {
  resolved = null;
}

/** The `currentReport()` flags `<common-filter>` is bound to in `reports.html`. */
export interface StandardReportBindings {
  hideDate?: boolean;
  hideClientMatter?: boolean;
  hideMatterTags?: boolean;
  showBilled?: boolean;
  showContingencyBilled?: boolean;
  revenueReport?: boolean;
  hideExpense?: boolean;
}

/** `hide-expense` really means "show the Show Expenses toggle". */
export function standardDescriptor(
  b: StandardReportBindings,
): ReportShowFilters {
  return {
    dateRange: !b.hideDate,
    matterForUser: !b.hideClientMatter,
    archivedStatus: !b.hideClientMatter,
    matterTags: !b.hideMatterTags,
    billed: Boolean(b.showBilled),
    contingencyBilled: Boolean(b.showContingencyBilled),
    distribution: Boolean(b.revenueReport),
    showExpense: Boolean(b.hideExpense),
  };
}

/** Standard reports hide drafts from users without the permission and list newest first. */
export function standardDistributions(
  distributions: ReportDistribution[],
  canSeeDrafts: boolean,
): ReportDistribution[] {
  return distributions
    .filter((d) => canSeeDrafts || d.status !== 'Draft')
    .sort((a, b) => +new Date(b.date) - +new Date(a.date));
}

export function trustAccountsOf(me: ReportPanelUser): ReportTrustAccount[] {
  const { settings } = me.company ?? {};
  if (!settings?.trustEnabled) return [];
  return [
    settings.trust?.operating,
    ...(settings.trust?.accounts ?? []).map((a) => ({
      id: a.bank.id,
      name: a.bank.name,
    })),
  ].filter((t): t is ReportTrustAccount => Boolean(t));
}

/**
 * Deliberately per-session, not per-visit. $state.go inherits dateRangeId, so a
 * dashboard card's period keeps arriving on every later transition inside Reports.
 * The period cards are how people actually open reports — the report name is a link
 * too, but the UI doesn't sell that — so a period picked in the sidebar is meant to
 * carry across reports until a *different* card period is clicked. Scoping this to
 * Report3Ctrl re-applies the card's period on every entry into Reports and defeats
 * that. This note is here because AI keeps flagging this as a bug, but it's more of
 * a niche design choice that we haven't addressed in 5+ years.
 */
export function createRoutePeriodTracker() {
  let consumed: string | null = null;
  return function applyRoutePeriod(
    store: ReportFiltersStore,
    range: string | null | undefined,
    id: string | null | undefined,
  ) {
    const period = store.periodFromRoute(range) || store.periodFromRoute(id);
    const key = period ? period.id || period.label : null;
    if (!period || key === consumed) return;
    consumed = key;
    store.setFilters({ dateRange: period });
  };
}

const LOCKED_ROLES = ['lead', 'timekeeper', 'paralegal'];

/**
 * "Show Matters Involving" options, then the role default: operators and accountants
 * see the whole firm, everyone else their own matters. Lead, timekeeper and paralegal
 * can only pick themselves unless they may auto-assign.
 */
export function loadPanelAccounts(
  store: ReportFiltersStore,
  me: ReportPanelUser,
  users: ReportUser[],
  formatDisabledUser: (accounts: ReportUser[]) => ReportUser[],
) {
  const locked =
    LOCKED_ROLES.includes(me.role) && !me.permissions.matters.autoAssign;
  const accounts = formatDisabledUser(locked ? [me] : users);
  store.configurePanel({ accounts, lockAccountSelection: locked });
  store.applyScopeAccountDefault(
    me,
    accounts,
    roleHelpers.isOperatorOrAccountantRole(me.role),
  );
}

export interface PanelContextSources {
  me: () => Promise<ReportPanelUser>;
  users: () => Promise<ReportUser[]>;
  distributions: () => Promise<{ distributions: ReportDistribution[] }>;
  formatDisabledUser: (accounts: ReportUser[]) => ReportUser[];
  /** False once the hosting directive is destroyed — the store is a singleton. */
  isAlive: () => boolean;
  onError: (error: unknown) => void;
}

/**
 * Loads what the panel shows beyond the filters. Accounts and distributions are
 * independent on purpose: a failed distributions call must not cost an attorney their
 * own-matters default.
 */
export function loadPanelContext(
  store: ReportFiltersStore,
  family: ReportFilterFamily,
  sources: PanelContextSources,
): Promise<unknown> {
  // Both chains wait on `me`, so its failure would otherwise be reported twice.
  const reported = new Set<unknown>();
  const guarded = <T>(work: Promise<T>, apply: (value: T) => void) =>
    work
      .then((value) => {
        if (sources.isAlive()) apply(value);
      })
      .catch((error) => {
        if (!sources.isAlive() || reported.has(error)) return;
        reported.add(error);
        sources.onError(error);
      });

  const me = sources.me();
  return Promise.all([
    guarded(Promise.all([me, sources.users()]), ([user, users]) => {
      if (family === 'generic') {
        const trustAccounts = trustAccountsOf(user);
        if (trustAccounts.length) {
          store.configurePanel({ trustAccounts });
          store.setTrustAccount(trustAccounts[0]);
        }
      }
      loadPanelAccounts(store, user, users, sources.formatDisabledUser);
    }),
    guarded(Promise.all([me, sources.distributions()]), ([user, list]) => {
      store.configurePanel({
        distributions:
          family === 'standard'
            ? standardDistributions(
                list.distributions,
                Boolean(user.permissions.billing?.distributions),
              )
            : list.distributions,
      });
    }),
  ]);
}

/** Calls back on any filter change, optionally debounced. Returns a stop function. */
export function onFiltersChange(
  store: ReportFiltersStore,
  callback: () => void,
  debounceMs = 0,
) {
  let pending: ReturnType<typeof setTimeout> | null = null;
  const stopWatching = watch(
    () => store.filters,
    () => {
      if (!debounceMs) {
        callback();
        return;
      }
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => {
        pending = null;
        callback();
      }, debounceMs);
    },
    { deep: true },
  );
  return () => {
    if (pending) clearTimeout(pending);
    stopWatching();
  };
}
